import { useState, useMemo, useEffect } from "react";
import { useSearchParams, useLocation, Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import CapsuleCard from "@/components/CapsuleCard";
import { capsulas } from "@/data/capsulas";
import { getAllCapsulas } from "@/data/capsulasGenerator";
import { blogPosts } from "@/data/blogPosts";
import { Search } from "lucide-react";
import Footer from "@/components/Footer";
import { useSEO } from "@/hooks/useSEO";

const Capsulas = () => {
  useSEO({
    title: "Herramientas IA - Soluciones probadas para tu negocio | Globaltis IA",
    description: "Descubre las herramientas de IA más avanzadas. Videos, automatizaciones, presentaciones y más. Aprende gratis o contrátanos para trabajos específicos.",
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Get all capsulas (manual + auto-generated)
  const allCapsulas = useMemo(() => getAllCapsulas(capsulas), []);

  // Separate manual and auto-generated
  const manualCapsulas = useMemo(
    () => allCapsulas.filter(c => (c as any).isManual),
    [allCapsulas]
  );
  
  const autoGeneratedCapsulas = useMemo(
    () => allCapsulas.filter(c => (c as any).isAutoGenerated),
    [allCapsulas]
  );

  // Set category from URL on mount
  useEffect(() => {
    const categoryParam = searchParams.get("category");
    if (categoryParam) {
      setSelectedCategory(categoryParam);
    }
  }, [searchParams]);

  // Scroll to capsule when hash is present
  useEffect(() => {
    if (location.hash) {
      const elementId = location.hash.substring(1);
      setTimeout(() => {
        const element = document.getElementById(elementId);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
          element.classList.add("ring-2", "ring-primary", "ring-offset-2");
          setTimeout(() => {
            element.classList.remove("ring-2", "ring-primary", "ring-offset-2");
          }, 2000);
        }
      }, 300);
    }
  }, [location.hash]);

  const categories = useMemo(() => {
    const cats = new Set(allCapsulas.map(c => c.category));
    return Array.from(cats);
  }, [allCapsulas]);

  const filterCapsulas = (capsulasToFilter: typeof allCapsulas) => {
    return capsulasToFilter.filter(capsula => {
      const matchesSearch = 
        capsula.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        capsula.description.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesCategory = !selectedCategory || capsula.category === selectedCategory;
      
      return matchesSearch && matchesCategory;
    });
  };

  const filteredManualCapsulas = useMemo(
    () => filterCapsulas(manualCapsulas),
    [manualCapsulas, searchTerm, selectedCategory]
  );

  const filteredAutoCapsulas = useMemo(
    () => filterCapsulas(autoGeneratedCapsulas),
    [autoGeneratedCapsulas, searchTerm, selectedCategory]
  );

  const getRelatedPosts = (postIds: string[]) => {
    return postIds
      .map(id => blogPosts.find(post => post.id === id))
      .filter(Boolean) as typeof blogPosts;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-secondary/5 to-background">
      {/* Hero Section */}
      <section className="py-16 md:py-24 px-4 text-center">
        <div className="max-w-4xl mx-auto space-y-6">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground">
            Las herramientas de IA más avanzadas
          </h1>
          <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto">
            Web de <span className="text-primary font-semibold">formación y actualidad</span> en IA. 
            Aprende las últimas tecnologías con nuestras guías{" "}
            <span className="text-foreground font-semibold">o contrátanos</span>{" "}
            para trabajos específicos.
          </p>
          <p className="text-base md:text-lg text-muted-foreground max-w-3xl mx-auto">
            Videos, presentaciones, propuestas, páginas web. 
            Descubre cómo aplicar estas herramientas para mejorar tus tiempos diarios. 
            Hazlo tú mismo con nuestras guías gratuitas o delégalo en nosotros.
          </p>
        </div>
      </section>

      {/* Search and Filter */}
      <section className="px-4 pb-12 max-w-6xl mx-auto">
        <div className="space-y-6">
          {/* Search Bar */}
          <div className="relative max-w-xl mx-auto">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-5 w-5" />
            <Input
              type="text"
              placeholder="Buscar soluciones (ej: videos, emails, WhatsApp...)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-12 text-base"
            />
          </div>

          {/* Category Filters */}
          <div className="flex flex-wrap justify-center gap-2">
            <Badge
              variant={selectedCategory === null ? "default" : "outline"}
              className="cursor-pointer px-4 py-2 text-sm"
              onClick={() => setSelectedCategory(null)}
            >
              Todas ({allCapsulas.length})
            </Badge>
            {categories.map((category) => (
              <Badge
                key={category}
                variant={selectedCategory === category ? "default" : "outline"}
                className="cursor-pointer px-4 py-2 text-sm"
                onClick={() => setSelectedCategory(category)}
              >
                {category} ({allCapsulas.filter(c => c.category === category).length})
              </Badge>
            ))}
          </div>
        </div>
      </section>

      {/* Capsulas Grid */}
      <section className="px-4 pb-20 max-w-7xl mx-auto space-y-16">
        {/* Manual Capsulas Section */}
        {filteredManualCapsulas.length > 0 && (
          <div className="space-y-8">
            <div className="text-center">
              <h2 className="text-3xl font-bold text-foreground mb-2">
                Soluciones Probadas
              </h2>
              <p className="text-muted-foreground">
                Herramientas que ya están funcionando en pequeñas empresas
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredManualCapsulas.map((capsula) => (
                <div key={capsula.id} id={capsula.id} className="transition-all duration-300">
                  <CapsuleCard
                    icon={capsula.icon}
                    title={capsula.title}
                    description={capsula.description}
                    difficulty={capsula.difficulty}
                    timeToImplement={capsula.timeToImplement}
                    relatedPosts={getRelatedPosts(capsula.relatedPostIds)}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Auto-Generated Capsulas Section */}
        {filteredAutoCapsulas.length > 0 && (
          <div className="space-y-8">
            <div className="text-center">
              <h2 className="text-3xl font-bold text-foreground mb-2 flex items-center justify-center gap-2">
                Recién Descubiertas
                <Badge className="bg-primary">Nuevo</Badge>
              </h2>
              <p className="text-muted-foreground">
                Soluciones generadas automáticamente de nuestro blog
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredAutoCapsulas.map((capsula) => (
                <div key={capsula.id} id={capsula.id} className="transition-all duration-300">
                  <CapsuleCard
                    icon={capsula.icon}
                    title={capsula.title}
                    description={capsula.description}
                    difficulty={capsula.difficulty}
                    timeToImplement={capsula.timeToImplement}
                    relatedPosts={getRelatedPosts(capsula.relatedPostIds)}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* No Results */}
        {filteredManualCapsulas.length === 0 && filteredAutoCapsulas.length === 0 && (
          <div className="text-center py-16">
            <p className="text-xl text-muted-foreground">
              No se encontraron cápsulas que coincidan con tu búsqueda.
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              Intenta con otros términos o explora todas las categorías.
            </p>
          </div>
        )}
      </section>

      {/* CTA Section */}
      <section className="py-24 px-8 bg-black text-white text-center">
        <div className="max-w-3xl mx-auto space-y-8">
          <h2 className="text-4xl md:text-5xl font-light">
            ¿Necesitas ayuda con alguna herramienta?
          </h2>
          <p className="text-xl text-white/70 font-light">
            Completa el formulario y cuéntanos qué necesitas.
          </p>
          <Link 
            to="/contacto?tipo=servicio"
            className="inline-flex px-10 py-5 bg-white text-black rounded-full hover:bg-gray-200 transition-all text-lg font-medium"
          >
            Ir al formulario
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Capsulas;
